import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { JackBaseComponentModule, jkLib } from "jk-service";
import { EasymdeMarkdownEditorComponent } from '../easymde-markdown-editor/easymde-markdown-editor.component';
import { MtrwlService } from '../../_service_mtr';
import { Mtrwl } from '../../_entity_mtr';
import { TiptapMarkdownEditorComponent } from '../tiptap-markdown-editor/tiptap-markdown-editor.component';

type SaveState = 'idle' | 'editing' | 'saving' | 'saved' | 'failed';

@Component({
  selector: 'app-notes',
  standalone: true,
  imports: [FormsModule, JackBaseComponentModule,
    TiptapMarkdownEditorComponent,
    EasymdeMarkdownEditorComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './notes.component.html',
  styleUrl: './notes.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NotesComponent implements OnInit, OnDestroy {
categoryChange($event: string) {
  this.dirty = true;
  this.category.set($event);
}
  //private readonly noteService = inject(NoteService);
  readonly mtrwlSrv = inject(MtrwlService);
  isTiptapEditor = signal(false);
  notes = signal<Mtrwl[]>([]);
  total = signal(0);
  visibleCount = signal(40);
  visibleNotes = computed(() => this.notes().slice(0, this.visibleCount()));
  currentNote = signal<Mtrwl | null>(null);
  subject = signal('');
  wl002 = signal('');
  wl003 = signal(99);
  wl005 = signal(jkLib.nowDate());
  wl015 = signal(0);
  content = signal('');
  category = signal('04');
  showArchiveModal = signal(false);
  //categoryOptions = CATEGORY_OPTIONS;
  query = signal('');
  starredOnly = signal(false);
  loading = signal(false);
  errorMessage = signal('');
  saveState = signal<SaveState>('idle');
  feedback = signal('');

  private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private savingPromise: Promise<void> | null = null;
  private editRevision = 0;
  private dirty = false;

  ngOnInit(): void {
    this.loadNotes();
  }

  ngOnDestroy(): void {
    if (this.autoSaveTimer !== null) clearTimeout(this.autoSaveTimer);
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.flushAutoSave().catch(() => {});
  }
  /*
  async loadNotes(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set('');
    try {
      const response = await this.mtrwlSrv.list(this.query(), this.starredOnly());
      this.notes.set(response.items);
      this.total.set(response.total);
      this.visibleCount.set(40);
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.loading.set(false);
    }
  }*/

  scheduleSearch(): void {
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadNotes(), 300);
  }
  /*
  async createNote(): Promise<void> {
    await this.flushAutoSave();
    this.errorMessage.set('');
    try {
      const note = await this.mtrwlSrv.create();
      this.notes.update((items) => [note, ...items]);
      this.total.update((value) => value + 1);
      this.openEditor(note);
      this.saveState.set('saved');
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    }
  }*/

  async openNote(note: Mtrwl): Promise<void> {
    await this.flushAutoSave();
    this.openEditor(note);
  }

  async showList(): Promise<void> {
    await this.flushAutoSave();
    this.currentNote.set(null);
    this.saveState.set('idle');
    await this.loadNotes();
  }

  editSubject(value: string): void {
    this.subject.set(value.slice(0, 100));
    this.markEdited();
  }
  editWL003(value: string): void {
    this.wl003.set(Number(value) );
    this.markEdited();
  }
  editWL005(value: string): void {
    this.wl005.set(value );
    this.markEdited();
  }
  editWL015(value: string | number): void {
    this.wl015.set(Number(value) || 0);
    this.markEdited();
  }

  toggleArchiveModal(): void {
    this.showArchiveModal.update((v) => !v);
  }

  // 歸檔方式 1：同一日期合併歸到同一分類單頭 (工作類)
  archiveSameDateToWorkCategory(): void {
    // TODO: 下次補規格實作 (同一日期合併歸到同一分類單頭 (工作類))
    console.log('歸檔方式：同一日期合併歸到同一分類單頭 (工作類)');
    alert('【歸檔-同一日期合併歸至工作類單頭】功能待實作');
    this.showArchiveModal.set(false);
  }

  // 歸檔方式 2：同一月份單身(日記)
  archiveSameMonthDiary(): void {
    // TODO: 下次補規格實作 (同一月份單身(日記))
    console.log('歸檔方式：同一月份單身(日記)');
    alert('【歸檔-同一月份單身(日記)】功能待實作');
    this.showArchiveModal.set(false);
  }

  editContent(value: string): void {
    this.content.set(value); // value.slice(0, 4000)
    this.markEdited();
  }

  editCategory(value: string): void {
    const validValue = value?.trim() || '04';
    this.category.set(validValue);
    this.markEdited();
  }

  getCategoryLabel(code?: string | null): string {

    const found = this.mtrwlSrv.mtrWd015List.find((item) => item.id === code);
    return found ? found.name : (code || '04 日記');
  }

  async flushAutoSave(): Promise<void> {
    if (this.autoSaveTimer !== null) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    await this.saveNow();
  }

  async retrySave(): Promise<void> {
    await this.saveNow();
  }

  async toggleStar(note: Mtrwl, event?: Event): Promise<void> {
    event?.stopPropagation();
    try {
      const updated = await this.mtrwlSrv.setStarred(note, note.WL007 !== 'Y');
      this.replaceNote(updated);
      if (this.currentNote()?.WL001 === updated.WL001 && this.currentNote()?.WL002 === updated.WL002) {
        this.currentNote.set(updated);
      }
      if (this.starredOnly() && updated.WL008 !== 'Y') {
        this.notes.update((items) => items.filter((item) => !this.sameNote(item, updated)));
      }
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    }
  }

  async deleteCurrent(): Promise<void> {
    const note = this.currentNote();
    if (!note || !window.confirm('確定要永久刪除這則筆記嗎？此操作無法復原。')) return;
    try {
      await this.mtrwlSrv.delete(note);
      this.notes.update((items) => items.filter((item) => !this.sameNote(item, note)));
      this.total.update((value) => Math.max(0, value - 1));
      this.currentNote.set(null);
      this.saveState.set('idle');
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    }
  }

  async copyContent(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.content());
      this.feedback.set('內文已複製');
    } catch {
      this.feedback.set('無法存取剪貼簿');
    }
    window.setTimeout(() => this.feedback.set(''), 2200);
  }

  onListScroll(event: Event): void {
    const element = event.currentTarget as HTMLElement;
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 120) {
      this.visibleCount.update((value) => Math.min(value + 40, this.notes().length));
    }
  }

  private openEditor(note: Mtrwl): void {
    this.currentNote.set(note);
    this.subject.set(note.WL007 || '');
    this.wl003.set(Number(note.WL003 || 99));
    this.wl005.set(note.WL005 || jkLib.nowDate());
    this.wl015.set(Number(note.WL015 || 0));
    this.content.set(note.WL013 || '');
    this.category.set(note.WL014 || '04');
    this.wl002.set(note.WL002 || '');
    this.dirty = false;
    this.saveState.set('saved');
    this.errorMessage.set('');
  }

  private markEdited(): void {
    if (!this.currentNote()) return;
    this.editRevision += 1;
    this.dirty = true;
    this.saveState.set('editing');
    if (this.autoSaveTimer !== null) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      this.saveNow();
    }, 800);
  }

  private async saveNow(): Promise<void> {
    if (this.savingPromise) await this.savingPromise;
    const note = this.currentNote();
    if (!note || !this.dirty) return;
    const revision = this.editRevision;
    const subject = this.subject();
    const wl003 = this.wl003();
    const wl005 = this.wl005();
    const wl015 = this.wl015();
    const content = this.content();
    const category = this.category() || '04';
    this.saveState.set('saving');
    this.savingPromise = (async () => {
      try {
        const updated = await this.mtrwlSrv.update(note, subject, wl003, wl005, content, category, wl015);
        this.replaceNote(updated);
        if (this.currentNote() && this.sameNote(this.currentNote()!, updated)) {
          this.currentNote.set(updated);
        }
        if (revision === this.editRevision) {
          this.dirty = false;
          this.saveState.set('saved');
        } else {
          this.saveState.set('editing');
        }
      } catch (error) {
        this.saveState.set('failed');
        this.errorMessage.set(error instanceof Error ? error.message : String(error));
      }
    })();
    await this.savingPromise;
    this.savingPromise = null;
    if (this.dirty && revision !== this.editRevision && this.autoSaveTimer === null) {
      this.autoSaveTimer = setTimeout(() => {
        this.autoSaveTimer = null;
        this.saveNow();
      }, 800);
    }
  }

  private replaceNote(note: Mtrwl): void {
    this.notes.update((items) => items.map((item) => this.sameNote(item, note) ? note : item));
  }

  private sameNote(left: Mtrwl, right: Mtrwl): boolean {
    return left.WL001 === right.WL001 && left.WL002 === right.WL002;
  }
//---------------------------------------------------------------------
  async createNote(): Promise<void> {
    await this.flushAutoSave();
    this.errorMessage.set('');
    try {
      const note = await this.mtrwlSrv.create('', '', this.wl014());
      //this.currentNote.set(note);
      this.notes.update((items) => [note, ...items]);
      this.total.update((value) => value + 1);

      this.subject.set(note.WL007 || '');
      this.wl003.set(Number(note.WL003 || 99));
      this.wl005.set(note.WL005 || jkLib.nowDate());
      this.content.set(note.WL013 || '');
      //this.wl014.set('04');
      this.openEditor(note);
      this.saveState.set('saved');
      this.dirty = false;
    } catch (error) {
      this.errorMessage.set(
        error instanceof Error ? error.message : String(error),
      );
    }
  }




  /*
  private markEdited(): void {
    if (!this.currentNote()) return;
    this.revision += 1;
    this.dirty = true;
    this.saveState.set('editing');
    if (this.autoSaveTimer !== null) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = null;
      this.saveNow();
    }, 800);
  }*/

  /*
  private async saveNow(): Promise<void> {
    if (this.savingPromise) await this.savingPromise;
    const note = this.currentNote();
    if (!note || !this.dirty) return;
    const revision = this.editRevision;
    this.saveState.set('saving');
    this.savingPromise = (async () => {
      try {
        const updated = await this.mtrwlSrv.update(
          note,
          this.subject(),
          this.content(),
          this.wl014(),
        );
        this.currentNote.set(updated);
        if (revision === this.editRevision) {
          this.dirty = false;
          this.saveState.set('saved');
        }
      } catch (error) {
        this.saveState.set('failed');
        this.errorMessage.set(
          error instanceof Error ? error.message : String(error),
        );
      }
    })();
    await this.savingPromise;
    this.savingPromise = null;
  }*/
  // notes--------------------------
  readonly wl014 = signal('04');
  async loadNotes(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set('');
    try {
      const response = await this.mtrwlSrv.getNotesList(this.query(), this.starredOnly());
      this.notes.set(response);
      this.total.set(response.length);
      this.visibleCount.set(40);
    } catch (error) {
      this.errorMessage.set(error instanceof Error ? error.message : String(error));
    } finally {
      this.loading.set(false);
    }
  }



}
